/**
 * BulkImportWizard.jsx
 * 
 * Admin component for bulk importing products via CSV/JSON files.
 * Features:
 * - File upload (drag & drop)
 * - Template selection
 * - Data preview
 * - Validation feedback
 * - Import progress
 * - Results summary
 */

import React, { useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { 
  Upload, 
  FileSpreadsheet, 
  FileJson, 
  Download, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Eye,
  FileUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step indicators
const STEPS = [
  { id: 1, label: 'Upload File' },
  { id: 2, label: 'Configure' },
  { id: 3, label: 'Preview' },
  { id: 4, label: 'Import' }
];

const BulkImportWizard = ({ open, onClose, templates = [], onImportComplete }) => {
  // Step state
  const [currentStep, setCurrentStep] = useState(1);
  
  // File state
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  // Config state
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  
  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  
  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Reset wizard
  const resetWizard = () => {
    setCurrentStep(1);
    setFile(null);
    setSelectedTemplate('');
    setSkipDuplicates(true);
    setPreviewData(null);
    setPreviewError(null);
    setImportResult(null);
  };
  
  // Handle close
  const handleClose = () => {
    resetWizard();
    onClose();
  };
  
  // File handling
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    validateAndSetFile(selectedFile);
  };
  
  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) return;
    
    const ext = selectedFile.name.toLowerCase().split('.').pop();
    if (!['csv', 'json'].includes(ext)) {
      setPreviewError('Please upload a CSV or JSON file');
      return;
    }
    
    setFile(selectedFile);
    setPreviewError(null);
  };
  
  // Preview data
  const loadPreview = async () => {
    if (!file) return;
    
    setPreviewLoading(true);
    setPreviewError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedTemplate) {
        formData.append('template_id', selectedTemplate);
      }
      
      const response = await fetch(`${API_URL}/api/bulk-import/preview`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Preview failed');
      }
      
      const data = await response.json();
      setPreviewData(data);
    } catch (error) {
      setPreviewError(error.message);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Import products
  const runImport = async () => {
    if (!file || !selectedTemplate) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('template_id', selectedTemplate);
      formData.append('skip_duplicates', skipDuplicates);
      formData.append('dry_run', 'false');
      
      const response = await fetch(`${API_URL}/api/bulk-import/import`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }
      
      const result = await response.json();
      setImportResult(result);
      
      if (result.imported > 0 && onImportComplete) {
        onImportComplete(result);
      }
    } catch (error) {
      setImportResult({
        success: false,
        errors: [{ error: error.message }]
      });
    } finally {
      setImporting(false);
    }
  };
  
  // Download template
  const downloadTemplate = async (format) => {
    const templateType = templates.find(t => t.id === selectedTemplate)?.product_type || 'LGF';
    window.open(`${API_URL}/api/bulk-import/template/${format}?product_type=${templateType}`, '_blank');
  };
  
  // Navigation
  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!file;
      case 2: return !!selectedTemplate;
      case 3: return previewData && previewData.valid;
      default: return false;
    }
  };
  
  const handleNext = async () => {
    if (currentStep === 2) {
      await loadPreview();
    }
    if (currentStep === 3) {
      await runImport();
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };
  
  // Render steps
  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}
          ${file ? 'border-green-500 bg-green-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.json"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {file ? (
          <div className="flex flex-col items-center gap-3">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
            <div>
              <p className="font-medium text-green-700">{file.name}</p>
              <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
              Change File
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className={`w-12 h-12 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
            <div>
              <p className="font-medium text-gray-700">Drop your file here</p>
              <p className="text-sm text-gray-500">or click to browse</p>
            </div>
            <div className="flex gap-2 mt-2">
              <Badge variant="outline" className="flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" /> CSV
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <FileJson className="w-3 h-3" /> JSON
              </Badge>
            </div>
          </div>
        )}
      </div>
      
      {/* Download Templates */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Download Sample Templates</CardTitle>
          <CardDescription className="text-xs">
            Use these templates as a starting point for your import file
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadTemplate('csv')}>
            <Download className="w-4 h-4 mr-2" />
            CSV Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadTemplate('json')}>
            <Download className="w-4 h-4 mr-2" />
            JSON Template
          </Button>
        </CardContent>
      </Card>
      
      {previewError && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4" />
          {previewError}
        </div>
      )}
    </div>
  );
  
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Template Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Product Template *</Label>
        <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
          <SelectTrigger data-testid="template-select">
            <SelectValue placeholder="Select a template for imported products" />
          </SelectTrigger>
          <SelectContent>
            {templates.map(template => (
              <SelectItem key={template.id} value={template.id}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{template.product_type}</Badge>
                  {template.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          All imported products will use this template for pricing and options
        </p>
      </div>
      
      {/* Options */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Import Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Skip Duplicates</Label>
              <p className="text-xs text-gray-500">Skip products with matching names</p>
            </div>
            <Switch
              checked={skipDuplicates}
              onCheckedChange={setSkipDuplicates}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* File Info */}
      {file && (
        <Card className="bg-gray-50">
          <CardContent className="py-3 flex items-center gap-3">
            <FileUp className="w-5 h-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm font-medium">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
  
  const renderStep3 = () => (
    <div className="space-y-4">
      {previewLoading ? (
        <div className="flex flex-col items-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="text-sm text-gray-500 mt-2">Analyzing file...</p>
        </div>
      ) : previewError ? (
        <div className="flex flex-col items-center py-8 text-red-600">
          <XCircle className="w-12 h-12" />
          <p className="mt-2">{previewError}</p>
        </div>
      ) : previewData ? (
        <>
          {/* Summary */}
          <div className="flex gap-4">
            <Card className={`flex-1 ${previewData.valid ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <CardContent className="py-4 text-center">
                {previewData.valid ? (
                  <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-500 mx-auto" />
                )}
                <p className="text-2xl font-bold mt-2">{previewData.total_rows}</p>
                <p className="text-sm text-gray-600">Products to Import</p>
              </CardContent>
            </Card>
            
            <Card className="flex-1">
              <CardContent className="py-4 text-center">
                <Eye className="w-8 h-8 text-blue-500 mx-auto" />
                <p className="text-2xl font-bold mt-2">{previewData.columns?.length || 0}</p>
                <p className="text-sm text-gray-600">Columns Detected</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Validation Errors */}
          {previewData.validation_errors?.length > 0 && (
            <Card className="border-yellow-200">
              <CardHeader className="py-2 bg-yellow-50">
                <CardTitle className="text-sm text-yellow-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Validation Issues ({previewData.validation_errors.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2 max-h-40 overflow-y-auto">
                {previewData.validation_errors.map((error, i) => (
                  <p key={i} className="text-xs text-yellow-700 py-1">{error}</p>
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Data Preview */}
          <Card>
            <CardHeader className="py-2">
              <CardTitle className="text-sm">Data Preview (First 5 Rows)</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    {previewData.columns?.slice(0, 6).map(col => (
                      <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                    {previewData.columns?.length > 6 && (
                      <th className="px-3 py-2 text-left font-medium text-gray-400">
                        +{previewData.columns.length - 6} more
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {previewData.sample_rows?.map((row, i) => (
                    <tr key={i} className="border-t">
                      {previewData.columns?.slice(0, 6).map(col => (
                        <td key={col} className="px-3 py-2 whitespace-nowrap max-w-[150px] truncate">
                          {row[col] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
  
  const renderStep4 = () => (
    <div className="space-y-4">
      {importing ? (
        <div className="flex flex-col items-center py-12">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
          <p className="text-lg font-medium mt-4">Importing Products...</p>
          <p className="text-sm text-gray-500">Please wait, this may take a moment</p>
        </div>
      ) : importResult ? (
        <>
          {/* Result Summary */}
          <Card className={importResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardContent className="py-6 text-center">
              {importResult.success ? (
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              ) : (
                <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              )}
              <h3 className="text-2xl font-bold mt-4">
                {importResult.success ? 'Import Successful!' : 'Import Failed'}
              </h3>
            </CardContent>
          </Card>
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-3xl font-bold text-gray-800">{importResult.total_rows || 0}</p>
                <p className="text-sm text-gray-500">Total Rows</p>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="py-4 text-center">
                <p className="text-3xl font-bold text-green-600">{importResult.imported || 0}</p>
                <p className="text-sm text-gray-500">Imported</p>
              </CardContent>
            </Card>
            <Card className={importResult.failed > 0 ? 'border-red-200' : ''}>
              <CardContent className="py-4 text-center">
                <p className={`text-3xl font-bold ${importResult.failed > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {importResult.failed || 0}
                </p>
                <p className="text-sm text-gray-500">Failed/Skipped</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Errors */}
          {importResult.errors?.length > 0 && (
            <Card className="border-yellow-200">
              <CardHeader className="py-2 bg-yellow-50">
                <CardTitle className="text-sm text-yellow-700">
                  Issues ({importResult.errors.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2 max-h-40 overflow-y-auto">
                {importResult.errors.map((err, i) => (
                  <p key={i} className="text-xs text-yellow-700 py-1">
                    {err.row ? `Row ${err.row}: ` : ''}{err.error}
                    {err.skipped && <Badge className="ml-2 text-xs">Skipped</Badge>}
                  </p>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5" />
            Bulk Product Import
          </DialogTitle>
          <DialogDescription>
            Import multiple products from a CSV or JSON file
          </DialogDescription>
        </DialogHeader>
        
        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6 px-4">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                  ${currentStep >= step.id 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
                  }
                `}>
                  {currentStep > step.id ? <CheckCircle2 className="w-5 h-5" /> : step.id}
                </div>
                <span className={`text-xs mt-1 ${currentStep >= step.id ? 'text-blue-600' : 'text-gray-400'}`}>
                  {step.label}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-blue-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Step Content */}
        <div className="min-h-[300px]">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>
        
        <DialogFooter className="flex justify-between">
          <div>
            {currentStep > 1 && currentStep < 4 && (
              <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>
              {currentStep === 4 ? 'Close' : 'Cancel'}
            </Button>
            {currentStep < 4 && (
              <Button 
                onClick={handleNext}
                disabled={!canProceed() || previewLoading || importing}
              >
                {previewLoading || importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {currentStep === 3 ? 'Start Import' : 'Next'}
                {currentStep < 3 && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            )}
            {currentStep === 4 && importResult?.success && (
              <Button onClick={() => { handleClose(); }}>
                Done
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportWizard;
